import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import uniqBy from "lodash/uniqBy";
import moment from "moment";
import qs from "qs";
import { useSearchParams } from "react-router-dom";

import callConstants from "constants/Call";
import { DEFAULT_PAGE_SIZE } from "constants/Constants";
import AuditService from "services/Audit";
import { isCustomTable } from "services/customTableType";

const { PENDING, PROCESSING } = callConstants.TRANSCRIPT_STATUSES;
const TRANSCRIPT_POLL_INTERVAL_MS = 10000;
// The recording lands, and the transcript row with it, some time after the
// call ends. The window is measured from when the call started, so it has to
// cover the call itself as well.
const AWAITING_TRANSCRIPT_WINDOW_MINUTES = 30;
// Measured from the transcript's last state change. A pending row older than
// its limit lost its task message and moves only on a manual requeue. The
// processing limit outlasts the backend job timeout (2 hours) plus the
// 15-minute sweep that marks a timed-out job failed.
const IN_PROGRESS_WINDOW_MINUTES = {
  [PENDING]: 30,
  [PROCESSING]: 150,
};

// Timestamps are serialized as naive UTC.
const isYoungerThan = (timestamp, minutes) =>
  moment.utc().diff(moment.utc(timestamp), "minutes") < minutes;

const isAwaitingTranscript = row => {
  if (row.auditable_type !== "CampaignTargetCall") return false;
  const status = row.call_info?.transcript_status;
  if (!status) {
    return isYoungerThan(row.created_at, AWAITING_TRANSCRIPT_WINDOW_MINUTES);
  }
  const maxAgeMinutes = IN_PROGRESS_WINDOW_MINUTES[status];
  return (
    Boolean(maxAgeMinutes) &&
    isYoungerThan(row.call_info.transcript_updated_at, maxAgeMinutes)
  );
};

/**
 * Transcription runs in the background and pushes nothing to the page, so the
 * feed polls while any loaded call may still change its transcript state. The
 * status is read live on the server, outside the activity-log cache.
 */
const transcriptPollInterval = query => {
  const rows = query.state.data?.pages.flatMap(page => page.data ?? []) ?? [];
  return rows.some(isAwaitingTranscript) ? TRANSCRIPT_POLL_INTERVAL_MS : false;
};

/**
 * A record's Activity Log feed: the entries loaded so far, newest first, and
 * the query string that selects them.
 *
 * Entries load in chunks as the reader scrolls. The filters live in the URL;
 * how far the reader has scrolled does not.
 *
 * `filterKeys` names the params the filter panel owns. The Activity Log does
 * not own the whole query string: the Power Dialer renders it on a route whose
 * own params (`private`, `callback`, `phone_number`, `voicemail_id`,
 * `original_call_id`) sit there for the life of the window.
 */
export function useAuditLogFeed({
  modelId,
  modelType,
  slug,
  auditLogId,
  updateFlag,
  filterKeys = [],
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();

  // `page` is dropped because the feed no longer pages through the URL; a
  // bookmark from before still carries one, and the next write removes it.
  const queryParams = useMemo(() => {
    const { page: _page, ...rest } = qs.parse(search);
    return rest;
  }, [search]);

  const writeParams = useCallback(
    next => setSearchParams(qs.stringify(next)),
    [setSearchParams]
  );

  const { data: entry, isError: entryFailed } = useQuery({
    queryKey: ["audit-log-entry", auditLogId, modelType, modelId, queryParams],
    queryFn: () =>
      AuditService.getAuditLogEntry({
        auditLogId,
        modelId,
        modelType,
        filters: queryParams,
      }),
    enabled: Boolean(auditLogId),
    // The feed waits on this lookup, and a missing entry answers 404 for good,
    // so the default retries would only hold the log on its skeleton.
    retry: false,
  });

  // An entry reached from a note's "See Original" button may be any distance
  // back, so the first chunk is sized to reach the page it sits on. Honored
  // once per entry: the entry query refetches whenever the filters change,
  // and re-anchoring then would contradict a filter change returning the feed
  // to the newest entries.
  const [anchor, setAnchor] = useState({ id: null, pages: null });
  if (auditLogId && entry && anchor.id !== auditLogId) {
    setAnchor({ id: auditLogId, pages: Number(entry.page) || 1 });
  }
  const anchorPages = anchor.id === auditLogId ? anchor.pages : null;
  // Loading before the entry resolves would render the newest entries and then
  // jump. A stale link fails the lookup, and the feed then starts at the top.
  const awaitingEntry =
    Boolean(auditLogId) && anchor.id !== auditLogId && !entryFailed;

  // A custom table's own Activity Log has a slug but no row id.
  const tableScope = isCustomTable(modelType) && Boolean(slug) && !modelId;
  const enabled =
    (Boolean(modelId) || tableScope) &&
    (updateFlag !== 0 || tableScope) &&
    !awaitingEntry;

  const {
    data,
    isSuccess,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
  } = useInfiniteQuery({
    // updateFlag is a counter the record page bumps after it saves; carrying
    // it in the key is what makes those saves reload the feed.
    queryKey: [
      "audit-log",
      modelType,
      slug ?? null,
      modelId,
      queryParams,
      updateFlag,
      anchorPages,
    ],
    queryFn: ({ pageParam }) =>
      AuditService.getAuditLogs({
        modelId,
        modelType,
        slug,
        page: pageParam.page,
        perPage: pageParam.perPage,
        filters: queryParams,
      }),
    initialPageParam: {
      page: 1,
      perPage: (anchorPages ?? 1) * DEFAULT_PAGE_SIZE,
    },
    // Every chunk is a whole number of DEFAULT_PAGE_SIZE pages, including an
    // anchored first chunk, so the next one starts on a page boundary.
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const loaded = lastPageParam.page * lastPageParam.perPage;
      if (loaded >= (lastPage.total ?? 0)) return undefined;
      return {
        page: loaded / DEFAULT_PAGE_SIZE + 1,
        perPage: DEFAULT_PAGE_SIZE,
      };
    },
    enabled,
    refetchInterval: transcriptPollInterval,
  });

  const ownedByPanel = useMemo(() => new Set(filterKeys), [filterKeys]);

  const applyFilters = useCallback(
    params => {
      // Anything on the route that the panel does not own is carried through,
      // or applying a filter would drop it from the URL.
      const carried = Object.fromEntries(
        Object.entries(queryParams).filter(([key]) => !ownedByPanel.has(key))
      );
      setAnchor({ id: auditLogId ?? null, pages: null });
      writeParams({ ...carried, ...params });
    },
    [writeParams, queryParams, ownedByPanel, auditLogId]
  );

  // A refetch re-requests every loaded chunk at its original offset. An entry
  // added since then shifts each chunk by one, so the entry at a chunk's
  // boundary comes back twice.
  const log = useMemo(
    () => uniqBy(data?.pages.flatMap(page => page.data ?? []) ?? [], "id"),
    [data]
  );

  return {
    log,
    loaded: isSuccess,
    // An older page failing keeps the pages already loaded; only a feed with
    // nothing to show has failed to load.
    isLoadError: isError && !data,
    isEmpty: isSuccess && (data.pages[0]?.total ?? 0) === 0,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isNextPageError: isFetchNextPageError,
    activeItemId: entry?.item?.id ?? null,
    queryParams,
    hasFilters: Object.keys(queryParams).some(key => ownedByPanel.has(key)),
    refetch,
    applyFilters,
  };
}
